import * as React from 'react'

import { addDays, addMonths, addWeeks, format, startOfMonth, startOfWeek } from 'date-fns'
import { es } from 'date-fns/locale'
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react'

import { BillingPeriod } from '@/components/ReservationContext'
import { cn } from '@/shared/lib/styles/class-merge'
import { Button } from '@/shared/ui/button'
import { Calendar } from '@/shared/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover'

export interface PeriodDatePickerProps {
    date?: Date
    period: BillingPeriod
    onSelect: (date: Date | undefined) => void
    className?: string
    disabled?: boolean
}

export function PeriodDatePicker({ date, period, onSelect, className, disabled = false }: PeriodDatePickerProps) {
    const [isOpen, setIsOpen] = React.useState(false)

    const getDisplayText = () => {
        if (!date) return 'Seleccionar fecha'

        switch (period) {
            case 'daily':
                return format(date, "dd 'de' MMMM, yyyy", { locale: es })
            case 'weekly':
                const weekStart = startOfWeek(date, { locale: es })
                const weekEnd = addDays(weekStart, 6)
                return `Semana del ${format(weekStart, 'dd MMM', { locale: es })} - ${format(weekEnd, 'dd MMM', { locale: es })}`
            case 'monthly':
                return format(date, 'MMMM yyyy', { locale: es })
            default:
                return format(date, 'PPP', { locale: es })
        }
    }

    const handleDateSelect = (selectedDate: Date | undefined) => {
        if (!selectedDate) return

        let adjustedDate = selectedDate

        // Adjust the date based on the period
        switch (period) {
            case 'weekly':
                // For weekly, set to the start of the week
                adjustedDate = startOfWeek(selectedDate, { locale: es })
                break
            case 'monthly':
                // For monthly, set to the start of the month
                adjustedDate = startOfMonth(selectedDate)
                break
            // For daily, use the selected date as is
        }

        onSelect(adjustedDate)
        setIsOpen(false)
    }

    const handleNavigation = (direction: 'prev' | 'next') => {
        if (!date) return

        let newDate: Date

        switch (period) {
            case 'daily':
                newDate = direction === 'prev' ? addDays(date, -1) : addDays(date, 1)
                break
            case 'weekly':
                newDate = direction === 'prev' ? addWeeks(date, -1) : addWeeks(date, 1)
                break
            case 'monthly':
                newDate = direction === 'prev' ? addMonths(date, -1) : addMonths(date, 1)
                break
            default:
                newDate = date
        }

        onSelect(newDate)
    }

    return (
        <div className='flex items-center gap-2'>
            <Button variant='outline' size='icon' onClick={() => handleNavigation('prev')} disabled={disabled}>
                <ChevronLeft className='h-4 w-4' />
            </Button>

            <Popover open={isOpen} onOpenChange={setIsOpen}>
                <PopoverTrigger asChild>
                    <Button
                        variant={'outline'}
                        className={cn(
                            'justify-start pr-4 text-left font-normal',
                            !date && 'text-muted-foreground',
                            className,
                        )}
                        disabled={disabled}
                    >
                        <CalendarIcon className='mr-2 h-4 w-4' />
                        {getDisplayText()}
                    </Button>
                </PopoverTrigger>
                <PopoverContent className='w-auto p-0' align='start'>
                    <Calendar
                        mode='single'
                        selected={date}
                        onSelect={handleDateSelect}
                        initialFocus
                        disabled={disabled}
                        locale={es}
                        footer={
                            <div className='border-t p-3'>
                                <Button
                                    variant='outline'
                                    className='w-full'
                                    onClick={() => {
                                        const today = new Date()
                                        handleDateSelect(today)
                                    }}
                                >
                                    <CalendarIcon className='mr-2 h-4 w-4' />
                                    Ir a hoy
                                </Button>
                            </div>
                        }
                    />
                </PopoverContent>
            </Popover>

            <Button variant='outline' size='icon' onClick={() => handleNavigation('next')} disabled={disabled}>
                <ChevronRight className='h-4 w-4' />
            </Button>
        </div>
    )
}
